import axios from 'axios';
import { auth } from '../firebase';
import { SubscriptionRecord } from '../types/subscription';
import { parseSubscriptionList, parseSubscriptionRecord } from '../util/subscriptionState';
import { authHeader } from './authHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const subscriptionService = {
  // Read the signed-in creator's own monthly price.
  // A 404 means "no price set yet", which is not an error.
  async getPlan(): Promise<{
    success: boolean;
    message: string;
    monthlyPriceCredits: number | null;
  }> {
    try {
      const headers = await authHeader();
      const creatorId = auth.currentUser?.uid;

      if (!headers || !creatorId) {
        return { success: false, message: 'Please log in again.', monthlyPriceCredits: null };
      }

      const response = await axios.get(`${API_URL}/api/subscriptions/plan/${creatorId}`, {
        headers,
      });

      const credits = response.data?.monthlyPriceCredits;
      return {
        success: true,
        message: '',
        monthlyPriceCredits: typeof credits === 'number' ? credits : null,
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { success: true, message: '', monthlyPriceCredits: null };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Could not load your subscription price',
        monthlyPriceCredits: null,
      };
    }
  },

  // Set the signed-in creator's monthly price (whole credits, at least 1).
  async updatePlan(monthlyPriceCredits: number): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      await axios.put(
        `${API_URL}/api/subscriptions/plan`,
        { monthlyPriceCredits },
        { headers },
      );

      return { success: true, message: 'Monthly price saved' };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to save your monthly price',
      };
    }
  },

  // Read another creator's monthly price, to price the subscribe button.
  // `plan: null` means "not open for subscriptions": no plan document (404), a
  // plan switched off, or a price the backend would refuse to charge. The
  // backend's own subscribe() rejects all three, so none of them may show a CTA.
  async getCreatorPlan(creatorId: string): Promise<{
    success: boolean;
    message: string;
    plan: { monthlyPriceCredits: number } | null;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.', plan: null };
      }

      const response = await axios.get(
        `${API_URL}/api/subscriptions/plan/${encodeURIComponent(creatorId)}`,
        { headers },
      );

      const credits = response.data?.monthlyPriceCredits;
      const usable =
        response.data?.active === true &&
        typeof credits === 'number' &&
        Number.isInteger(credits) &&
        credits >= 1;

      return {
        success: true,
        message: '',
        plan: usable ? { monthlyPriceCredits: credits } : null,
      };
    } catch (error: any) {
      // A creator who never set a price is not an error, just not subscribable.
      if (error.response?.status === 404) {
        return { success: true, message: '', plan: null };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Could not load this creator’s plan',
        plan: null,
      };
    }
  },

  // Every subscription the signed-in fan holds. The backend takes the fan from
  // the token, so there is nothing to pass.
  async listMine(): Promise<{
    success: boolean;
    message: string;
    subscriptions: SubscriptionRecord[];
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.', subscriptions: [] };
      }

      const response = await axios.get(`${API_URL}/api/subscriptions/mine`, { headers });

      return {
        success: true,
        message: '',
        subscriptions: parseSubscriptionList(response.data),
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Could not load your subscriptions',
        subscriptions: [],
      };
    }
  },

  // Subscribe to a creator. The backend charges the credits in its own
  // transaction; the HTTP status comes back so the caller can tell
  // "not enough credits" (400) from "already subscribed" (409).
  async subscribe(creatorId: string): Promise<{
    success: boolean;
    message: string;
    status?: number;
    subscription?: SubscriptionRecord | null;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      const response = await axios.post(
        `${API_URL}/api/subscriptions/${encodeURIComponent(creatorId)}`,
        undefined,
        { headers },
      );

      return {
        success: true,
        message: 'Subscribed',
        status: response.status,
        subscription: parseSubscriptionRecord(response.data),
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to subscribe',
        status: error.response?.status,
      };
    }
  },

  // Cancel at the end of the paid period: access lasts until currentPeriodEnd
  // and no credits come back. Cancelling twice is not an error on the backend.
  async cancel(creatorId: string): Promise<{
    success: boolean;
    message: string;
    status?: number;
    subscription?: SubscriptionRecord | null;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      const response = await axios.delete(
        `${API_URL}/api/subscriptions/${encodeURIComponent(creatorId)}`,
        { headers },
      );

      return {
        success: true,
        message: 'Subscription cancelled',
        status: response.status,
        subscription: parseSubscriptionRecord(response.data),
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to cancel',
        status: error.response?.status,
      };
    }
  },
};
